import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SmoothScroll = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.8,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      smoothTouch: false,
      syncTouch: false,
    });

    // Connect Lenis to GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    // Named reference required for correct cleanup
    const tickerCallback = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(tickerCallback);
    gsap.ticker.lagSmoothing(0);

    window.lenis = lenis;

    return () => {
      lenis.off('scroll', ScrollTrigger.update);
      gsap.ticker.remove(tickerCallback);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  // Scroll to top + refresh Lenis limit + ScrollTrigger on route change.
  // Multiple delays cover async content loads at any speed (ProductGrid, images, etc.)
  // that change the page height AFTER the initial render — if Lenis calculates its
  // scroll limit before that content loads, it clamps scroll too early.
  useEffect(() => {
    const lenis = window.lenis;
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }

    const refresh = () => {
      lenis?.resize?.();
      ScrollTrigger.refresh();
    };

    // Staggered refreshes: covers fast cached loads (50ms) through slow fetches (1000ms)
    const timeouts = [50, 250, 600, 1100].map(delay => setTimeout(refresh, delay));

    return () => timeouts.forEach(clearTimeout);
  }, [location.pathname]);

  return children;
};

export default SmoothScroll;
